// organize-imports-ignore: `./instrument` must stay the first import. ES
// modules evaluate in import order, and Sentry has to be initialised before
// the App module graph runs, because `wrapReactRouterRouting` (App.tsx) wraps
// <Routes> at module-evaluation time and silently returns the unwrapped
// component when the router integration is not registered yet.
import "./instrument"
import { PublicClientApplication } from "@azure/msal-browser"
import { MsalProvider } from "@azure/msal-react"
import * as Sentry from "@sentry/react"
import { QueryClientProvider } from "@tanstack/react-query"
import { Amplify } from "aws-amplify"
import React from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter as Router } from "react-router"
import App from "./App"
import awsconfig from "./aws-exports"
import { LanguageAwareAuthenticatorProvider } from "./components/feature/Authenticaton/LanguageAwareAuthenticatorProvider"
import { captureReferralSource } from "./features/ariba-onboarding/referralSource"
import {
  type CatalogLanguage,
  loadCatalog,
} from "./hooks/useTranslation/catalogs"
import {
  getDefaultLanguage,
  TranslationProvider,
} from "./hooks/useTranslation/useTranslation"
import "./index.css"
import { MotionProvider } from "./lib/motion"
import { LoadingSpinner } from "./components/common/LoadingSpinner/LoadingSpinner"
import msalconfig from "./msal-config"
import "./services/analytics/amplitude"
import { installConnectivityWatch } from "./services/api/connectivity"
import { createQueryClient } from "./services/api/createQueryClient"

Amplify.configure(awsconfig)
installConnectivityWatch()
// Before any redirect drops the query: the signup link carries utm_source.
captureReferralSource()
const queryClient = createQueryClient()

// Vite fires this when a hashed chunk 404s — typically because a deploy replaced the
// build's assets while this tab still holds a reference to an old chunk hash (e.g. a
// lazy-loaded route or component). Reload once to pick up the fresh index.html/manifest
// instead of leaving the user stuck on the ErrorBoundary's generic error screen. The
// sessionStorage guard stops a genuinely broken import (not a stale deploy) from
// reloading forever.
window.addEventListener("vite:preloadError", () => {
  const reloadedKey = "vitePreloadErrorReloaded"
  if (sessionStorage.getItem(reloadedKey)) return
  sessionStorage.setItem(reloadedKey, "1")
  window.location.reload()
})

// MSAL configuration
const msalConfig = {
  auth: {
    clientId: msalconfig.msal_client_id, // Your Azure app registration's Client ID
    authority: msalconfig.msal_authority, // Your Azure AD tenant ID
    redirectUri: msalconfig.msal_redirect_uri, // Your Redirect URI
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true, // Set this to true if you encounter issues with cookies
  },
}

// Initialize MSAL instance only in secure contexts (HTTPS/localhost)
// window.crypto.subtle is unavailable over plain HTTP, which causes MSAL to crash
const msalInstance = window.crypto?.subtle
  ? new PublicClientApplication(msalConfig)
  : null

const rootElement = document.getElementById("root")
if (!rootElement) {
  throw new Error(
    "Failed to find the root element to mount the React application.",
  )
}

// Errors caught by an error boundary are reported by ErrorBoundary's own
// componentDidCatch, so only uncaught and recoverable errors are wired here
// (adding onCaughtError as well would report boundary errors twice).
const root = ReactDOM.createRoot(rootElement, {
  // Sentry 10 records errors from reactErrorHandler() as unhandled (the session
  // counts as crashed) and those from a handler with a callback as handled.
  // Uncaught errors did take the app down; React recovered from recoverable
  // ones, so those get a no-op callback to stay handled, as under Sentry 9.
  onUncaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(() => {}),
})

const AppTree = (
  <LanguageAwareAuthenticatorProvider>
    {/* useTransitions={false}: keeps v6 behaviour (the app never enabled the
        v7_startTransition flag). React Router otherwise wraps every router
        update in startTransition, which lets local state commit before the
        URL does and breaks components that derive state from search params
        during render (e.g. InboxView re-picking a facet). */}
    <Router useTransitions={false}>
      <MotionProvider>
        <App />
      </MotionProvider>
    </Router>
  </LanguageAwareAuthenticatorProvider>
)

// A spinner until the UI language's catalog is in, so the page is never
// blank while the chunk loads.
root.render(
  <div className="flex h-dvh w-full items-center justify-center">
    <LoadingSpinner />
  </div>,
)

// Render the app once the UI language's catalog is loaded — rendering first
// would flash raw translation keys. If it fails, render anyway: keys beat a
// blank page (a stale-deploy 404 already reloads via vite:preloadError above).
loadCatalog(getDefaultLanguage() as CatalogLanguage)
  .catch((error: unknown) => Sentry.captureException(error))
  .then(() =>
    root.render(
      <React.StrictMode>
        <TranslationProvider>
          <QueryClientProvider client={queryClient}>
            {msalInstance ? (
              <MsalProvider instance={msalInstance}>{AppTree}</MsalProvider>
            ) : (
              AppTree
            )}
          </QueryClientProvider>
        </TranslationProvider>
      </React.StrictMode>,
    ),
  )
